import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import TuneIcon from '@mui/icons-material/Tune';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import WorkerCard from '../components/WorkerCard';
import { workerAPI } from '../api/endpoints';
import './SearchResults.css';

const FILTER_KEYS = ['query', 'category', 'zone', 'min_rating', 'min_experience', 'service'];

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workers, setWorkers] = useState([]);
  const [options, setOptions] = useState({ categories: [], services: [], zones: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState('');
  const [optionsReload, setOptionsReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resultsReload, setResultsReload] = useState(0);
  const [count, setCount] = useState(0);
  const queryString = searchParams.toString();

  const filters = {
    query: searchParams.get('query') || '',
    category: searchParams.get('category') || '',
    zone: searchParams.get('zone') || '',
    min_rating: searchParams.get('min_rating') || '',
    min_experience: searchParams.get('min_experience') || '',
    service: searchParams.get('service') || '',
  };

  useEffect(() => {
    let active = true;
    setOptionsLoading(true);
    setOptionsError('');
    const params = filters.category ? { category: filters.category } : {};
    workerAPI.filterOptions(params)
      .then((r) => {
        if (active) setOptions(r.data);
      })
      .catch(() => {
        if (active) setOptionsError('No se pudieron cargar las opciones de filtros.');
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });
    return () => { active = false; };
  }, [filters.category, optionsReload]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const params = {};
    const currentParams = new URLSearchParams(queryString);
    FILTER_KEYS.forEach((key) => {
      const value = currentParams.get(key);
      if (value) params[key] = value;
    });
    workerAPI
      .search(params)
      .then((r) => {
        if (!active) return;
        const data = r.data;
        setWorkers(data.results ?? data);
        setCount(data.count ?? (data.results ?? data).length);
      })
      .catch(() => {
        if (!active) return;
        setWorkers([]);
        setCount(0);
        setError('No se pudieron cargar los profesionales. Inténtalo de nuevo.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [queryString, resultsReload]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === 'category') next.delete('service');
    setSearchParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    const query = searchParams.get('query');
    if (query) next.set('query', query);
    setSearchParams(next);
  };

  return (
    <div className="results-page">
      <div className="container results-layout">
        {/* Filtros */}
        <aside className="filters">
          <div className="filters-head">
            <TuneIcon sx={{ fontSize: 20 }} />
            <h3>Filtros</h3>
            <button className="filters-clear" onClick={clearFilters}>
              Limpiar
            </button>
          </div>

          {optionsLoading && <p role="status">Cargando filtros...</p>}
          {optionsError && (
            <div className="form-alert error" role="alert">
              <p>{optionsError}</p>
              <button className="filters-clear" onClick={() => setOptionsReload((n) => n + 1)}>
                Reintentar
              </button>
            </div>
          )}

          <div className="filter-group">
            <label htmlFor="filter-category">Categoría</label>
            <select
              id="filter-category"
              value={filters.category}
              disabled={optionsLoading || !!optionsError}
              onChange={(e) => updateFilter('category', e.target.value)}
            >
              <option value="">Todas</option>
              {options.categories.map((c) => (
                <option key={c.id} value={filters.category === c.slug ? c.slug : c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="filter-zone">Zona de trabajo</label>
            <select
              id="filter-zone"
              value={filters.zone}
              disabled={optionsLoading || !!optionsError}
              onChange={(e) => updateFilter('zone', e.target.value)}
            >
              <option value="">Todas</option>
              {options.zones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="filter-service">Servicio ofrecido</label>
            <select
              id="filter-service"
              value={filters.service}
              disabled={optionsLoading || !!optionsError}
              onChange={(e) => updateFilter('service', e.target.value)}
            >
              <option value="">Todos</option>
              {options.services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="filter-rating">Calificación mínima</label>
            <select
              id="filter-rating"
              value={filters.min_rating}
              onChange={(e) => updateFilter('min_rating', e.target.value)}
            >
              <option value="">Cualquiera</option>
              <option value="4.5">4.5★ o más</option>
              <option value="4">4★ o más</option>
              <option value="3">3★ o más</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="filter-experience">Años de experiencia</label>
            <select
              id="filter-experience"
              value={filters.min_experience}
              onChange={(e) => updateFilter('min_experience', e.target.value)}
            >
              <option value="">Cualquiera</option>
              <option value="2">2+ años</option>
              <option value="5">5+ años</option>
              <option value="10">10+ años</option>
              <option value="15">15+ años</option>
            </select>
          </div>
        </aside>

        {/* Resultados */}
        <section className="results-content">
          <div className="results-header">
            <h2>
              {filters.query
                ? `Resultados para "${filters.query}"`
                : 'Maestros y obreros disponibles'}
            </h2>
            {!loading && !error && <span>{count} profesionales encontrados</span>}
          </div>

          {loading ? (
            <div className="results-loading">Cargando profesionales...</div>
          ) : error ? (
            <div className="results-empty" role="alert">
              <p>{error}</p>
              <button className="filters-clear" onClick={() => setResultsReload((n) => n + 1)}>
                Reintentar
              </button>
            </div>
          ) : workers.length === 0 ? (
            <div className="results-empty">
              <SentimentDissatisfiedIcon sx={{ fontSize: 60, color: '#9aa7b8' }} />
              <h3>No encontramos profesionales</h3>
              <p>Prueba con otra palabra clave o ajusta los filtros.</p>
            </div>
          ) : (
            <div className="results-grid">
              {workers.map((w) => (
                <WorkerCard key={w.id} worker={w} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SearchResults;
